import React, {useRef, useCallback, useEffect} from 'react';
import {QualifiedMap, getName} from '@packages/systems/wfdl-Qualified';
import {type NormalizedAtomConfig, Component} from '@packages/systems/wfdl';
import {
  DefaultStyle,
  atomConfigToDefaultStyleConfig,
  symbolRootToDefaultStyleConfig,
} from '@packages/systems/style/engine';
import {CSSOMWriter} from '@packages/systems/style/writers';
import {Breakpoint} from '@packages/systems/style/media-queries';
import {type BreakpointsMap} from '@packages/systems/style/types';
import {WebflowEnvironments} from '@packages/systems/core-constants';

type Props = {
  atoms: QualifiedMap<Component.ComponentName, NormalizedAtomConfig>;
  componentMap: Component.ComponentMap;
  breakpoints: BreakpointsMap<Breakpoint>;
};

const CanvasDefaultStyles = React.memo<Props>(
  ({atoms, componentMap, breakpoints}) => {
    const defaultStyleRef = useRef<DefaultStyle<HTMLStyleElement> | undefined>(
      undefined
    );

    const styleNodeRef = useCallback(
      (node: HTMLStyleElement) => {
        if (node) {
          defaultStyleRef.current = new DefaultStyle(
            new CSSOMWriter(node),
            Object.values(breakpoints),
            WebflowEnvironments.DESIGNER
          );
        }
      },
      [breakpoints]
    );

    useEffect(() => {
      const currentDefaultStyleRef = defaultStyleRef.current;
      if (currentDefaultStyleRef) {
        atoms.forEach((atom, key) => {
          currentDefaultStyleRef.addDefaultStyle(
            Component.nameToString(getName(key)),
            atomConfigToDefaultStyleConfig(atom)
          );
        });

        componentMap.forEach((component, key) => {
          currentDefaultStyleRef.addDefaultStyle(
            Component.nameToString(getName(key)),
            symbolRootToDefaultStyleConfig(getName(key), component)
          );
        });

        currentDefaultStyleRef.commit();
      }
    }, [atoms, componentMap]);

    return <style data-meta="default styles" ref={styleNodeRef} />;
  }
);

export default CanvasDefaultStyles;
